import { type DeskLayout, type SplitAxis } from './deskLayout';

/** Рабочий стол, на котором лежит вкладка сканера — не сумма всех мониторов. */
export type Desk = {
  availLeft: number;
  availTop: number;
  availWidth: number;
  availHeight: number;
};

/** Уже половины не хватает, чтобы две формы ставки стояли рядом. */
export const MIN_PANE = 520;
/** Стопка сверху-снизу: ниже форма ставки не читается. */
export const MIN_STACK = 360;
/** Третье окно — карточка вилки, не контора. */
export const CALC_W = 720;
export const CALC_H = 640;
/** Колонка вилки справа, как у открытия из бота. Ширина та же, рост — весь стол. */
export const FORK_COL_W = 380;

/** DOM `Screen` не знает `availLeft`/`availTop` — это поле живого Chrome. */
export type ScreenBox = {
  availLeft?: number;
  availTop?: number;
  availWidth?: number;
  availHeight?: number;
  width?: number;
  height?: number;
};

export function deskOf(screen: ScreenBox): Desk {
  return {
    availLeft: screen.availLeft || 0,
    availTop: screen.availTop || 0,
    availWidth: screen.availWidth || screen.width || 0,
    availHeight: screen.availHeight || screen.height || 0,
  };
}

/**
 * Карточка вилки узкая, стол широкий. Раскладку считаем по монитору,
 * иначе «открыть обе» с попапа уходит во вкладки как с телефона.
 */
export function deskView(desk: Desk, viewW: number, viewH: number): { w: number; h: number } {
  if (viewW < MIN_PANE && desk.availWidth >= MIN_PANE * 2) {
    return { w: desk.availWidth, h: Math.max(viewH, desk.availHeight) };
  }
  return { w: viewW, h: viewH };
}

/**
 * Полоса монитора под одну контору. `null` — места нет, остаёмся во вкладках.
 * Последняя полоса забирает остаток, чтобы не было щели в пиксель.
 * `stack` — сверху и снизу, как у THEFORKS; `side` — слева и справа (дефолт).
 */
export function splitPane(
  desk: Desk,
  index: number,
  count: number,
  axis: SplitAxis = 'side',
): Desk | null {
  if (count < 2 || index < 0 || index >= count) return null;
  if (axis === 'stack') {
    if (desk.availHeight < MIN_STACK * count) return null;
    const height = Math.floor(desk.availHeight / count);
    const top = desk.availTop + height * index;
    const pane = index === count - 1 ? desk.availHeight - height * (count - 1) : height;
    return {
      availLeft: desk.availLeft,
      availTop: top,
      availWidth: desk.availWidth,
      availHeight: pane,
    };
  }
  if (desk.availWidth < MIN_PANE * count) return null;
  const width = Math.floor(desk.availWidth / count);
  const left = desk.availLeft + width * index;
  const pane = index === count - 1 ? desk.availWidth - width * (count - 1) : width;
  return {
    availLeft: left,
    availTop: desk.availTop,
    availWidth: pane,
    availHeight: desk.availHeight,
  };
}

/** Карточка вилки поверх двух контор: по центру, не вместо половины. */
export function calcPane(desk: Desk): Desk {
  const width = Math.min(CALC_W, desk.availWidth);
  const height = Math.min(CALC_H, desk.availHeight);
  return {
    availLeft: desk.availLeft + Math.round((desk.availWidth - width) / 2),
    availTop: desk.availTop + Math.round((desk.availHeight - height) / 2),
    availWidth: width,
    availHeight: height,
  };
}

/** Вилка справа от двух контор. `null` — на узком столе колонке нет места. */
export function forkColumn(desk: Desk): Desk | null {
  if (desk.availWidth < MIN_PANE * 2 + FORK_COL_W || desk.availHeight < MIN_STACK) return null;
  return {
    availLeft: desk.availLeft + desk.availWidth - FORK_COL_W,
    availTop: desk.availTop,
    availWidth: FORK_COL_W,
    availHeight: desk.availHeight,
  };
}

/** Стол контор, когда справа уже занята колонка вилки. */
export function bookDeskOf(desk: Desk, column: Desk): Desk {
  return { ...desk, availWidth: desk.availWidth - column.availWidth };
}

export function paneFeatures(pane: Desk): string {
  return `popup=yes,width=${Math.round(pane.availWidth)},height=${Math.round(pane.availHeight)},left=${Math.round(pane.availLeft)},top=${Math.round(pane.availTop)}`;
}

/**
 * Полосы только там, где и монитор, и окно сканера широкие.
 * `screen.availWidth` на телефоне иногда врёт (режим «компьютер», планшет
 * лёжа): без `innerWidth` половины открываются там, где нужны вкладки.
 */
export function splitAllowed(
  desk: Desk,
  count: number,
  view: number,
  axis: SplitAxis = 'side',
  viewH: number = view,
): boolean {
  if (axis === 'stack') {
    return splitPane(desk, 0, count, 'stack') !== null && viewH >= MIN_STACK * count;
  }
  return splitPane(desk, 0, count, 'side') !== null && view >= MIN_PANE * count;
}

/**
 * Что человек выбрал в студии, и что экран реально пустит.
 * `auto` + `side` — прежнее правило: широкий стол делит, узкий остаётся во вкладках.
 */
export function resolveSplit(
  desk: Desk,
  count: number,
  prefs: DeskLayout,
  viewW: number,
  viewH: number,
): { split: boolean; axis: SplitAxis } {
  if (prefs.mode === 'tabs' || count < 2) return { split: false, axis: prefs.axis };
  const prefer = prefs.axis;
  const other: SplitAxis = prefer === 'stack' ? 'side' : 'stack';
  if (prefs.mode === 'auto' && prefer === 'side') {
    return { split: splitAllowed(desk, count, viewW), axis: 'side' };
  }
  if (splitAllowed(desk, count, viewW, prefer, viewH)) return { split: true, axis: prefer };
  if (prefs.mode === 'split' && splitAllowed(desk, count, viewW, other, viewH)) {
    return { split: true, axis: other };
  }
  if (prefs.mode === 'auto') {
    return { split: splitAllowed(desk, count, viewW, 'side', viewH), axis: 'side' };
  }
  return { split: false, axis: prefer };
}
